/**
 * 注册页面
 */

import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Form, Input, Button, Card, message, Typography, Steps } from 'antd'
import { UserOutlined, MailOutlined, LockOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useAuthStore } from '../store/authStore'
import './Register.css'

const { Title, Text } = Typography
const { Step } = Steps

const Register: React.FC = () => {
  const navigate = useNavigate()
  const { register, isAuthenticated, isLoading, error, clearError } = useAuthStore()
  const [form] = Form.useForm()
  const [currentStep, setCurrentStep] = useState(0)
  const [registerSuccess, setRegisterSuccess] = useState(false)

  useEffect(() => {
    // 如果已登录，跳转到首页
    if (isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    // 显示错误信息
    if (error) {
      message.error(error)
      clearError()
    }
  }, [error, clearError])

  const validatePassword = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('请输入密码'))
    }
    if (value.length < 6) {
      return Promise.reject(new Error('密码长度至少6个字符'))
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])|(?=.*[a-z])(?=.*\d)|(?=.*[A-Z])(?=.*\d)/.test(value)) {
      return Promise.reject(new Error('密码需包含字母和数字的组合'))
    }
    return Promise.resolve()
  }

  const validateConfirmPassword = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('请确认密码'))
    }
    if (value !== form.getFieldValue('password')) {
      return Promise.reject(new Error('两次输入的密码不一致'))
    }
    return Promise.resolve()
  }

  const handleSubmit = async (values: { 
    username: string
    email: string
    password: string
    confirm: string
  }) => {
    try {
      setCurrentStep(1)
      
      await register(values.username, values.email, values.password)
      
      setCurrentStep(2)
      setRegisterSuccess(true)
      message.success('注册成功！即将跳转到登录页面...')
      
      // 3秒后跳转到登录页面
      setTimeout(() => {
        navigate('/login', { replace: true })
      }, 3000)
      
    } catch (error: any) {
      setCurrentStep(0)
      // 错误已在store中处理
    }
  }

  return (
    <div className="register-container">
      <div className="register-background">
        <div className="register-gradient"></div>
      </div>
      
      <Card className="register-card">
        <div className="register-header">
          <div className="register-logo">📚</div>
          <Title level={2} className="register-title">创建账号</Title>
          <Text className="register-subtitle">加入CrawlBox，开始智能管理你的知识库</Text>
        </div>

        {!registerSuccess ? (
          <>
            <Steps current={currentStep} className="register-steps">
              <Step title="填写信息" icon={<UserOutlined />} />
              <Step title="创建账号" icon={<LockOutlined />} />
              <Step title="注册成功" icon={<CheckCircleOutlined />} />
            </Steps>

            <Form
              form={form}
              name="register"
              onFinish={handleSubmit}
              autoComplete="off"
              size="large"
              layout="vertical"
            >
              <Form.Item
                name="username"
                label="用户名"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, message: '用户名至少3个字符' },
                  { max: 20, message: '用户名最多20个字符' },
                  { 
                    pattern: /^[a-zA-Z0-9_]+$/, 
                    message: '用户名只能包含字母、数字和下划线' 
                  }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="请输入用户名（3-20个字符）"
                  autoComplete="username"
                />
              </Form.Item>

              <Form.Item
                name="email"
                label="邮箱"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="请输入邮箱地址"
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="密码"
                rules={[
                  { validator: validatePassword }
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="请输入密码（至少6个字符，包含字母和数字）"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item
                name="confirm"
                label="确认密码"
                dependencies={['password']}
                rules={[
                  { validator: validateConfirmPassword }
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="请再次输入密码"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  block
                  className="register-button"
                >
                  注册
                </Button>
              </Form.Item>

              <div className="register-footer">
                <Text>已有账号？</Text>
                <Link to="/login" className="register-link">立即登录</Link>
              </div>

              <div className="register-agreement">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  注册即表示同意
                  <a href="/terms" target="_blank"> 用户协议 </a>
                  和
                  <a href="/privacy" target="_blank"> 隐私政策</a>
                </Text>
              </div>
            </Form>
          </>
        ) : (
          <div className="register-success">
            <CheckCircleOutlined className="success-icon" />
            <Title level={3}>注册成功！</Title>
            <Text>您的账号已创建成功，即将跳转到登录页面...</Text>
            <Button 
              type="primary" 
              onClick={() => navigate('/login')}
              className="success-button"
            >
              立即登录
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}

export default Register

